/// <amd-module name="Wit/HtmlEditor/Components/HtmlEditor" />

import { AutocompleteComponent } from "Mention/Autocomplete/Components/AutocompleteComponent";
import * as React from "react";
import {
    Browser,
    ContentChangedPlugin,
    DoubleClickImagePlugin,
    Editor,
    EditorPlugin,
    EditorViewState,
    EmojiPaneProps,
    EmojiPlugin,
    EmojiPluginOptions,
    FocusEventHandler,
    FocusOutShell,
    fromHtml,
    getIconOnRenderDelegate,
    HtmlSanitizer,
    IgnorePasteImagePlugin,
    ImageManager,
    ImageManagerOptions,
    ImageResize,
    isNodeEmpty,
    LeanRooster,
    LeanRoosterModes,
    PasteImagePlugin,
    Position,
    RoosterCommandBar,
    RoosterCommandBarButton,
    RoosterCommandBarPlugin,
    RoosterCommandBarPluginOptions,
    RoosterCommmandBarButtonKeys,
    RoosterShortcutCommands,
    TableResize,
    UndoWithImagePlugin
} from "roosterjs-react";
import { GlobalMessageLevel, IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { announce } from "VSS/Core/Util/Accessibility";
import { format } from "VSS/Core/Util/String";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { css, getSafeId } from "VSSUI/Util";
import { CommandBarLocaleStrings, SearchForEmoji } from "Wit/HtmlEditor/Components/HtmlEditor.Strings";
import {
    EditorProviderPlugin,
    EscapeCodeBlockPlugin,
    IEditorProvider,
    IMentionPlugin,
    KeyDownPlugin,
    MentionPlugin,
    TabToNextFocusableElementPlugin,
    WorkItemLinkPlugin
} from "Wit/HtmlEditor/Plugins";
import * as Resources from "Wit/HtmlEditor/Resources";
import { createHTMLEditorInputProxy } from "./HtmlEditorInputProxy";
import { FormItemContext } from "VSSUI/FormItem";

const HyperlinkShortcut = Browser.isMac ? Resources.CommandClickToOpen : Resources.CtrlClickToOpen;

// by default, there is HTML bloat/workaround for Edge and Chrome around lists (only when setting specific font)
// to avoid the bloat, we're disabling this until we allow setting font
const DisableListWorkaround = true;

const PlaceholderImageClassName = "html-editor-img-placeholder";
const ExcludePlaceholderSelector = `:not(.${PlaceholderImageClassName})`;

// we currently don't support file drop
const PreventFileDropHandler = (ev: React.DragEvent<HTMLElement>) => {
    const { dataTransfer } = ev.nativeEvent;
    if (!dataTransfer || !dataTransfer.types) {
        return;
    }

    const fileType = "Files";
    const types = dataTransfer.types as any;
    // IE11 has contains but not indexOf
    if ((types.indexOf && types.indexOf(fileType) >= 0) || (types.contains && types.contains(fileType))) {
        ev.preventDefault();
        try {
            dataTransfer.dropEffect = "none"; // IE11 throws an error
        } catch (_) {}

        return false;
    }
};

export interface IHtmlEditorProps {
    htmlContent: string;

    className?: string;
    fullScreen?: boolean;
    handleXmvwitAnchor?: boolean;
    helpText?: string;
    hidden?: boolean;
    disableMentions?: boolean;
    onChange?: (content: string) => void;
    placeholder?: string;
    readonly?: boolean;
    showChromeBorder?: boolean;
    telemetryContext?: ITelemetryContext;
    uploadImageHandler?: (file: File) => Promise<string>;
    ariaLabel?: string;
    height?: number;
    onBlur?: FocusEventHandler;
    onFocus?: FocusEventHandler;
    onKeyDown?: (event: KeyboardEvent) => void;

    /**
     * If true, the editor is always in edit mode (i.e., LeanRoosterModes.Edit)
     * @default false
     */
    editOnlyMode?: boolean;

    /**
     * If true, the editor is focussed once component is mounted.
     */
    autoFocus?: boolean;
}

interface ITelemetryContext {
    controlName?: string;
}

export interface IHtmlEditorState {
    fullScreen: boolean;
    hidden: boolean;
    readonly: boolean;
}

export class HtmlEditor extends VssComponent<IHtmlEditorProps, IHtmlEditorState> {
    private _leanRoosterRef = React.createRef<LeanRooster>();
    private _leanRoosterContentDiv: HTMLDivElement | null;

    private _commandBarRef = React.createRef<RoosterCommandBar>();
    private _commandBarPlugin: RoosterCommandBarPlugin;
    private _imageResizePlugin: ImageResize;
    private _timerManagement: TimerManagement | null = new TimerManagement();
    private _throttledOnContentChanged: (updatedContent: string, isInitializing?: boolean) => void;
    private _editorPlugins: EditorPlugin[];
    private _emojiPlugin: EmojiPlugin | null;
    private _undoPlugin: UndoWithImagePlugin;
    private _imageManager: ImageManager;
    private _buttonOverrides: RoosterCommandBarButton[];
    private _creationStartTime: number | null;
    private _viewState: EditorViewState;
    private _mentionPlugin?: IMentionPlugin;
    private _editorProvider?: IEditorProvider;
    private _autocompleteIsSuggesting = new ObservableValue<boolean>(false);
    private _overflowMenuEventListener: (ev: KeyboardEvent) => void;
    private _overflowMenuComponent: any;

    public constructor(props: IHtmlEditorProps, context?: IVssComponentContext) {
        super(props, context);

        this._throttledOnContentChanged = this._timerManagement!.throttle(this._handleContentChanged, 100, { trailing: true });

        const { htmlContent, readonly, hidden, fullScreen } = props;
        this._viewState = { content: htmlContent, isDirty: false } as EditorViewState;
        this._createAdditionalEditorPlugins();
        this._initializeButtonOverrides();
        this._creationStartTime = Date.now();

        const viewState = this._viewState;
        const santizedContent = HtmlSanitizer.sanitizeHtml(viewState.content, MentionPlugin.SanitizeHtmlOptions);
        viewState.content = santizedContent;

        this.state = { readonly: !!readonly, hidden: !!hidden, fullScreen: !!fullScreen };

        this._overflowMenuEventListener = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                this._overflowMenuComponent?.dismiss();
            }
        };
    }

    public getValue(): string {
        return this._viewState.content;
    }

    public isEmpty(): boolean {
        return this.getValue() === "";
    }

    public render(): JSX.Element {
        const { className, height } = this.props;
        return (
            <div
                className={css(
                    "html-editor",
                    className,
                    this.state.hidden && "is-hidden",
                    this.state.fullScreen && "full-screen",
                    height == null && "auto-grow"
                )}
                style={{ height }}
            >
                {this._renderRoosterEditor()}
            </div>
        );
    }

    public setContent(newContent: string): void {
        // If the module is not loaded, just set the state, we will sanitize when the module has been loaded
        const content = HtmlSanitizer.sanitizeHtml(newContent, MentionPlugin.SanitizeHtmlOptions);
        if (this._tryUpdateState(content) && this._leanRoosterRef.current) {
            this._leanRoosterRef.current.reloadContent(true /* triggerContentChangedEvent */, false /* resetUndo */);
            const initialContent = this._leanRoosterRef.current.getContent();
            this._undoPlugin.reset(initialContent);
        }
    }

    public get isDirty(): boolean {
        return this._viewState.isDirty;
    }

    public get htmlContent(): string {
        return this._viewState.content;
    }

    public focus(): void {
        if (this._leanRoosterRef.current) {
            this._leanRoosterRef.current.focus();
        }
    }

    public selectAll(): void {
        if (this._leanRoosterRef.current) {
            this._leanRoosterRef.current.selectAll();
        }
    }

    public flushChanges(): void {
        if (this._leanRoosterRef.current) {
            // Force the content changed event bypassing the throttling to ensure that all changes are processed before saving the workitem.
            const content = this._leanRoosterRef.current.getContent();
            this._handleContentChanged(content);
        }
    }

    // force update on command bar will cause reflow, debounce here for all callers
    public refreshCommandBar = this._timerManagement!.debounce(() => this._commandBarRef.current && this._commandBarRef.current.forceUpdate(), 100, {
        trailing: true
    });

    public hasFocus(): boolean {
        return this._leanRoosterRef.current != null && this.mode === LeanRoosterModes.Edit;
    }

    public setEnabled(enabled: boolean): void {
        this.setState({ readonly: !enabled });
    }

    public setVisible(visible: boolean): void {
        this.setState({ hidden: !visible });
    }

    public setFullScreen(fullScreen: boolean): void {
        this.setState({ fullScreen });
    }

    public get mode(): LeanRoosterModes | undefined {
        return this._leanRoosterRef.current ? this._leanRoosterRef.current.mode : undefined;
    }

    public set mode(value: LeanRoosterModes | undefined) {
        if (this._leanRoosterRef.current && value !== undefined) {
            this._leanRoosterRef.current.mode = value;
        }
    }

    public componentDidMount(): void {
        if (this._leanRoosterRef.current && this.props.editOnlyMode) {
            this.mode = LeanRoosterModes.Edit;
        }

        if (this.props.autoFocus) {
            this.focus();
        }

        this._endCreateScenario();

        document.addEventListener("keydown", this._overflowMenuEventListener);
    }

    public componentWillUnmount(): void {
        const leanRoosterContentDiv = this._leanRoosterContentDiv;
        if (leanRoosterContentDiv) {
            this._leanRoosterContentDiv = null;
            this._emojiPlugin = null; // set to null to avoid race with _loadEmojiStrings(); also note rooster will call dispose on its plugins
        }

        if (this._timerManagement) {
            this._timerManagement.dispose();
            this._timerManagement = null;
        }

        if (this._commandBarPlugin && this._commandBarRef.current) {
            this._commandBarPlugin.unregisterRoosterCommandBar(this._commandBarRef.current);
        }

        document.removeEventListener("keydown", this._overflowMenuEventListener);
    }

    public componentDidUpdate(prevProps: Readonly<IHtmlEditorProps>, prevState: Readonly<IHtmlEditorState>, snapshot?: {}): void {
        super.componentDidUpdate(prevProps, prevState, snapshot);

        if (this.props.htmlContent !== prevProps.htmlContent && this.props.htmlContent !== this.htmlContent) {
            // The htmlContent prop has changed, and the new value does not match what is in the editor. Update the editor to match.
            this.setContent(this.props.htmlContent);
        }

        if (this.props.readonly !== prevProps.readonly && this.props.readonly !== this.state.readonly) {
            this.setState({ readonly: !!this.props.readonly });
        }
    }

    private _renderRoosterEditor(): JSX.Element | null {
        const { showChromeBorder, placeholder, ariaLabel } = this.props;

        return (
            <>
                <FocusOutShell
                    className={css("rooster-wrapper", !!showChromeBorder && "with-chrome")}
                    allowMouseDown={this._focusOutShellAllowMouseDown}
                    onBlur={this._focusOutShellOnBlur}
                    onFocus={this._focusOutShellOnFocus}
                >
                    {(calloutClassName: string, calloutOnDismiss: FocusEventHandler) => {
                        this._createPluginsWithCallout(calloutClassName, calloutOnDismiss);
                        return [
                            <FormItemContext.Consumer key="rooster-consumer">
                                {formItemContext => {
                                    return (
                                        <LeanRooster
                                            key="rooster"
                                            className={css("rooster-editor", "propagate-keydown-event", "text-element")}
                                            viewState={this._viewState}
                                            plugins={this._editorPlugins}
                                            undo={this._undoPlugin}
                                            ref={this._leanRoosterRef}
                                            updateViewState={this._updateViewState}
                                            contentDivRef={this._leanRoosterContentDivOnRef}
                                            readonly={this.state.readonly}
                                            placeholder={placeholder}
                                            sanitizeAttributeCallbacks={MentionPlugin.SanitizeHtmlOptions.attributeCallbacks}
                                            hyperlinkToolTipCallback={this._hyperlinkToolTipCallback}
                                            onFocus={this._leanRoosterOnFocus}
                                            onDragEnter={PreventFileDropHandler}
                                            onDragOver={PreventFileDropHandler}
                                            aria-label={ariaLabel}
                                            aria-labelledby={getSafeId(formItemContext.ariaLabelledById)}
                                            aria-describedby={getSafeId(formItemContext.ariaDescribedById)}
                                            activateRoosterOnMount={!this.props.disableMentions}
                                            activateInViewMode={!this.props.disableMentions}
                                            onHyperlinkClick={this._onHyperlinkClick}
                                            clickOpenHyperlinkViewMode={true}
                                        />
                                    );
                                }}
                            </FormItemContext.Consumer>,
                            <RoosterCommandBar
                                key="cmd"
                                className="rooster-command-bar"
                                commandBarClassName="html-command-bar-base"
                                buttonOverrides={this._buttonOverrides}
                                roosterCommandBarPlugin={this._commandBarPlugin}
                                emojiPlugin={this._emojiPlugin!}
                                imageManager={this._imageManager}
                                calloutClassName={css("rooster-callout", calloutClassName)}
                                calloutOnDismiss={calloutOnDismiss}
                                strings={CommandBarLocaleStrings}
                                ref={this._commandBarRef}
                                onButtonClicked={this._publishToolbarTelemetry}
                                disableListWorkaround={DisableListWorkaround}
                                overflowMenuProps={{
                                    className: "rooster-command-bar-overflow",
                                    componentRef: ref => this._overflowMenuComponent = ref,
                                }}
                                ellipsisAriaLabel={Resources.Toolbar_More}
                            />
                        ];
                    }}
                </FocusOutShell>
                <AutocompleteComponent
                    insertAsHtml
                    isSuggesting={this._autocompleteIsSuggesting}
                    ref={autocompleteComponent => {
                        if (this._leanRoosterContentDiv) {
                            autocompleteComponent?.bind(
                                createHTMLEditorInputProxy(this._leanRoosterContentDiv, () => this._editorProvider?.getEditor())
                            );
                        }
                    }}
                />
            </>
        );
    }

    private _createPluginsWithCallout(calloutClassName: string, calloutOnDismiss: FocusEventHandler): any {
        if (!this._emojiPlugin) {
            this._emojiPlugin = new EmojiPlugin({
                calloutClassName: css(calloutClassName, "rooster-emoji-callout"),
                calloutOnDismiss,
                emojiPaneProps: {
                    quickPickerClassName: "rooster-emoji-quick-pick",
                    navBarProps: {
                        className: "rooster-emoji-navbar",
                        buttonClassName: "rooster-emoji-navbar-button",
                        iconClassName: "rooster-emoji-navbar-icon",
                        selectedButtonClassName: "rooster-emoji-navbar-selected"
                    },
                    statusBarProps: {
                        className: "rooster-emoji-status-bar"
                    },
                    emojiIconProps: {
                        className: "rooster-emoji-icon",
                        selectedClassName: "rooster-emoji-selected"
                    },
                    searchPlaceholder: SearchForEmoji,
                    searchInputAriaLabel: SearchForEmoji
                } as EmojiPaneProps,
                onKeyboardTriggered: this._publishEmojiFromKeyboardTelemetry,
                strings: { emjDNoSuggetions: Resources.NoSuggestionsFound } //  only load a small portion of required strings first (the rest is async loaded)
            } as EmojiPluginOptions);
            this._editorPlugins.push(this._emojiPlugin);
        }

        if (!this._commandBarPlugin) {
            this._commandBarPlugin = new RoosterCommandBarPlugin({
                strings: CommandBarLocaleStrings,
                onShortcutTriggered: this._publishShortcutCommandTelemetry,
                disableListWorkaround: DisableListWorkaround,
                calloutClassName,
                calloutOnDismiss
            } as RoosterCommandBarPluginOptions);
            this._editorPlugins.push(this._commandBarPlugin);
        }

        if (this.props.onKeyDown) {
            this._editorPlugins.push(new KeyDownPlugin(this.props.onKeyDown));
        }
    }

    private _initializeButtonOverrides(): void {
        const supportInsertImage = !!this.props.uploadImageHandler;
        const keys = RoosterCommmandBarButtonKeys;
        const buttonClassName = "html-command-bar-button";
        this._buttonOverrides = [
            { key: keys.Bold, order: 0 },
            { key: keys.Italic, order: 1 },
            { key: keys.Underline, order: 2 },
            { key: keys.BulletedList, order: 3 },
            { key: keys.NumberedList, order: 4 },
            { key: keys.Highlight, order: 5, subMenuPropsOverride: { ariaLabel: Resources.HighlightColorPicker_AriaLabel } },
            { key: keys.FontColor, order: 6, subMenuPropsOverride: { ariaLabel: Resources.FontColorPicker_AriaLabel } },
            { key: keys.Emoji, order: 7, buttonClassName: css("rooster-emoji-button", buttonClassName) },
            { key: keys.Outdent, order: 8 },
            { key: keys.Indent, order: 9 },
            { key: keys.Strikethrough, order: 10 },
            { key: keys.Header, order: 11, subMenuPropsOverride: { ariaLabel: Resources.HeaderMenu_AriaLabel } },
            { key: keys.Code, order: 12 },
            { key: keys.ClearFormat, order: 16 },
            { key: keys.InsertImage, order: 17, exclude: !supportInsertImage },
            { key: keys.Link, order: 18 },
            { key: keys.Unlink, order: 19 }
        ];

        if (!this.props.disableMentions) {
            this._buttonOverrides.push({
                key: "atMention", // HtmlEditorStringsModule.CustomButtonKeys.AtMention,
                name: Resources.MentionSomeone,
                iconProps: { iconName: "Accounts" },
                onRender: getIconOnRenderDelegate(),
                handleChange: this._createMentionButttonHandler("@"),
                order: 13
            });
            this._buttonOverrides.push({
                key: "hashMention", // HtmlEditorStringsModule.CustomButtonKeys.HashMention,
                name: Resources.MentionWorkItem,
                iconProps: { iconName: "NumberSymbol" },
                onRender: getIconOnRenderDelegate(),
                handleChange: this._createMentionButttonHandler("#"),
                order: 14
            });
            this._buttonOverrides.push({
                key: "prMention", // HtmlEditorStringsModule.CustomButtonKeys.PRMention,
                name: Resources.MentionPullRequest,
                iconProps: { iconName: "BranchPullRequest" },
                onRender: getIconOnRenderDelegate(),
                handleChange: this._createMentionButttonHandler("!"),
                order: 15
            });
        }

        this._buttonOverrides.forEach(b => {
            b.className = b.className || "html-command-button-root";
            b.buttonClassName = b.buttonClassName || buttonClassName;
        });
    }

    /**
     * Returns a callback that will insert the specified character and open the mention autocomplete menu
     * @param mentionChar the character like `@`, `#`, or `!` which will trigger mention autocomplete
     */
    private _createMentionButttonHandler = (mentionChar: string) => (editor: Editor) => {
        const selection = editor.getSelection();

        // Insert the mention character, preceded by whitespace if there is not already whitespace
        const alreadyHasWhitespace = /\s/.test(selection?.focusNode?.textContent?.substr(-1) ?? "");
        editor.insertContent(alreadyHasWhitespace ? mentionChar : ` ${mentionChar}`);
        editor.triggerContentChangedEvent("Mentions");

        // The autocomplete control relies on checking the current selection to decide whether to open the
        // autocomplete menu. Unfortunately, the roosterJS editor will sometimes (but not always!) insert new
        // content in a new text node instead of appending to the content of the current text node. This means when
        // we check the selection, we will sometimes see our focus in an empty text node even though we just
        // inserted a mention character. Fun! The solution is just to look at previous sibling nodes until we see
        // one that contains the mention character, and then set focus on the position after that character.
        editor.focus();
        const currentSelection = editor.getSelection();
        // Go to the previous sibling until we have a node that contains the mention character
        let focusNode = currentSelection.focusNode!; // the node that contains the start of the current selection.
        let mentionCharIndex = focusNode.textContent?.lastIndexOf(mentionChar) ?? -1;
        while (mentionCharIndex === -1 && focusNode.previousSibling) {
            focusNode = focusNode.previousSibling;
            mentionCharIndex = focusNode.textContent?.lastIndexOf(mentionChar) ?? -1;
        }

        if (mentionCharIndex == -1) {
            // This should never happen. If we can't find the node in which we inserted the mention character,
            // don't bother triggering the input event because the autocomplete control won't know what to do with
            // the selection.
            return;
        }

        // Put the cursor on the position just after the mention character we just inserted
        editor.select(new Position(focusNode, mentionCharIndex + 1));

        // Triggering an "input" event will cause the autocomplete control to check the selection and open
        this._leanRoosterContentDiv?.dispatchEvent(new InputEvent("input"));
    };

    private _createAdditionalEditorPlugins(): void {
        const { uploadImageHandler: uploadImage, handleXmvwitAnchor } = this.props;

        this._imageManager = new ImageManager({
            uploadImage,
            placeholderImageClassName: PlaceholderImageClassName
        } as ImageManagerOptions);
        this._undoPlugin = new UndoWithImagePlugin(this._imageManager);
        this._imageResizePlugin = new ImageResize(undefined, undefined, undefined, undefined, ExcludePlaceholderSelector);

        const supportInsertImage: boolean = !!uploadImage;
        this._editorPlugins = [
            new ContentChangedPlugin(this._throttledOnContentChanged),
            this._imageResizePlugin,
            new TableResize(),
            supportInsertImage ? new PasteImagePlugin(this._imageManager) : IgnorePasteImagePlugin.Instance,
            new DoubleClickImagePlugin(ExcludePlaceholderSelector),
            new EscapeCodeBlockPlugin(),
            new TabToNextFocusableElementPlugin(),
            new WorkItemLinkPlugin(this.context.pageContext)
        ];

        if (!this.props.disableMentions) {
            const editorProviderPlugin = new EditorProviderPlugin();
            this._editorProvider = editorProviderPlugin;
            this._editorPlugins.push(editorProviderPlugin);

            this._mentionPlugin = new MentionPlugin(this.context.pageContext, this._autocompleteIsSuggesting);
            this._editorPlugins.push(this._mentionPlugin);
        }
    }

    private _tryUpdateState(newContent: string, isInitializing: boolean = false): boolean {
        newContent = this._isEmpty(newContent) ? "" : newContent;
        const viewState = this._viewState;
        if (newContent !== viewState.content) {
            viewState.content = newContent;
            viewState.isDirty = true;

            return !isInitializing;
        }

        return false;
    }

    private _isEmpty(html: string): boolean {
        const ISEMPTY_MINIMAL_CONTENT_LENGTH = 500;
        if (html == null || html.length === 0) {
            return true;
        }

        if (html.length >= ISEMPTY_MINIMAL_CONTENT_LENGTH) {
            return false;
        }

        const tempNode = fromHtml(`<div>${html}</div>`, document)[0] as HTMLElement;
        return isNodeEmpty(tempNode);
    }

    private _endCreateScenario() {
        if (this._creationStartTime) {
            // const contentLength = (this._viewState.content || "").length;
            // const elapsedTime = Date.now() - this._creationStartTime;
            // publishEvent(
            //     new TelemetryEventData(TelemetryAreaName, TelemetryCreateScenario, {
            //         elapsedTime,
            //         contentLength,
            //     })
            // );
            this._creationStartTime = null;
        }
    }

    private _publishEmojiFromKeyboardTelemetry = (): void => {
        this._publishShortcutCommandTelemetry("Emoji");
    };

    private _publishToolbarTelemetry = (command: string): void => {
        const controlName = (this.props.telemetryContext || ({} as ITelemetryContext)).controlName;
        // publishEvent(
        //     new TelemetryEventData(TelemetryAreaName, ToolbarButtonClickFeature, {
        //         controlName,
        //         command
        //     })
        // );
    };

    private _publishShortcutCommandTelemetry = (command: RoosterShortcutCommands | string): void => {
        const controlName = (this.props.telemetryContext || ({} as ITelemetryContext)).controlName;
        // publishEvent(
        //     new TelemetryEventData(TelemetryAreaName, ShortcutCommandFeature, {
        //         controlName,
        //         command
        //     })
        // );
    };

    private _handleContentChanged = (content: string, isInitializing: boolean = false): void => {
        if (this._tryUpdateState(content, isInitializing) && this.props.onChange) {
            this.props.onChange(this.isEmpty() ? "" : content);
        }
    };

    private _updateViewState = (existingViewState: EditorViewState, content: string, isInitializing: boolean): void => {
        this._throttledOnContentChanged(content, isInitializing);
    };

    private _focusOutShellAllowMouseDown = (element: HTMLElement): boolean => {
        const isContenteditable = !!(this._leanRoosterContentDiv && this._leanRoosterContentDiv.contains(element));

        return isContenteditable;
    };

    private _focusOutShellOnFocus = (ev: React.FocusEvent<HTMLElement>): void => {
        if (this._commandBarRef.current) {
            this._commandBarPlugin.registerRoosterCommandBar(this._commandBarRef.current); // re-register command b/c we're changing mode on blur
        }

        if (!this.props.editOnlyMode && this._leanRoosterRef) {
            this.mode = LeanRoosterModes.Edit;
        }
    };

    private _leanRoosterOnFocus = (ev: React.FocusEvent<HTMLElement>): void => {
        this.props.onFocus?.(ev);

        const { helpText } = this.props;
        if (helpText && this._leanRoosterRef.current && this._leanRoosterRef.current.hasPlaceholder()) {
            announce(helpText, true);
        }

        this._mentionPlugin?.onFocus();
    };

    private _focusOutShellOnBlur = (ev: React.FocusEvent<HTMLElement>): void => {
        if (this.props.onBlur) {
            this.props.onBlur(ev);
        }

        if (!this.props.editOnlyMode && this._leanRoosterRef) {
            this.mode = LeanRoosterModes.View;
            this._imageResizePlugin.hideResizeHandle();
        }

        this._mentionPlugin?.onBlur();
    };

    private _leanRoosterContentDivOnRef = (ref: HTMLDivElement): void => {
        this._leanRoosterContentDiv = ref;
        this._loadEmojiStrings();
    };

    private _hyperlinkToolTipCallback = (href: string): string => {
        // roosterjs will support which link to process in the future, for now we don't show tooltip
        // href having just hash (@ mention)
        if (href.replace(location.href, "") === "#") {
            return "";
        }

        if (this.hasFocus()) {
            return `${href}\n${HyperlinkShortcut}`;
        }

        return "";
    };

    private _onHyperlinkClick = (anchor: HTMLAnchorElement, ev: MouseEvent): boolean => {
        const href = anchor.getAttribute("href");
        if (this.mode !== LeanRoosterModes.View || !href) {
            return false; // let the built-in Hyperlink plugin handle edit mode
        }

        try {
            window.open(href, "_blank");
        } catch (error) {
            this.reportError(error);
        }

        return true;
    };

    private reportError(ex: Error | string): void {
        const globalMessageService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        const status = (ex as any).status || 0;
        const message = format(Resources.OperationFailed, `${ex instanceof Error ? ex.message : ex}${status ? " (" + status + ")" : ""}`);

        globalMessageService.addBanner({
            message,
            dismissable: true,
            level: GlobalMessageLevel.error
        });
    }

    private _loadEmojiStrings(): void {
        // VSS.using(EmojiStringsModuleList, (EmojiStrings: typeof EmojiStrings_Async): void => this._emojiPlugin && this._emojiPlugin.setStrings(EmojiStrings.EmojiLocaleStrings));
    }
}

Components.add("wit-html-editor-component", HtmlEditor);
